import { CoachSettings, Prompt } from "../interfaces";
import { AppUserContext } from "../providers";
import { HashTagsServices, SettingsServices } from "../services";
import { PlayersServices } from "../services/bedrock";

export interface CaptionArgs {
  shortLink?: string;
  hashtags?: string[];
  complianceDisclaimer?: string;
}

const getCaptionSettings = async (userContext: AppUserContext, prompt?: Prompt) => {
  var includeShortLink = prompt?.allowShortLink !== false;

  var includeDisclaimer: boolean | undefined;
  if ((userContext.profile?.complianceDisclaimer?.length || 0) === 0) includeDisclaimer = false;
  else if (prompt?.allowComplianceDisclaimer) includeDisclaimer = true;

  var includeHashtags: boolean | undefined;
  if (userContext.player?.addHashTagAutomatically === false) includeHashtags = false;
  else if (prompt?.allowHashtags === false) includeHashtags = false;

  const coachId = userContext.subscriptions?.[0]?.pricePlan?.coachId;
  if (coachId && (includeHashtags === undefined || includeDisclaimer === undefined)) {
    const { settings } = await SettingsServices.getAppSettingsByCoachId(coachId);
    if (includeDisclaimer === undefined) {
      includeDisclaimer =
        (settings.find(s => s.appSettingId === CoachSettings.COMPLIANCE_DISCLAIMER)?.status || "ACTIVE") === "ACTIVE";
    }
    if (includeHashtags === undefined) {
      includeHashtags =
        (settings.find(s => s.appSettingId === CoachSettings.PLAYER_HASHTAG)?.status || "ACTIVE") === "ACTIVE";
    }
  }

  return {
    includeHashtags,
    includeDisclaimer,
    includeShortLink,
  };
};

export const getCaptionArgs = async (userContext: AppUserContext, prompt?: Prompt): Promise<CaptionArgs> => {
  return new Promise(async (resolve, reject) => {
    const playerId = userContext.player?.id;
    if (!playerId) {
      reject("Player ID not found");
      return;
    }

    var settings = await getCaptionSettings(userContext, prompt);
    var userHashtags: string[] = [];
    var shortLink: string | undefined;
    const promises = [];

    if (settings.includeShortLink) {
      promises.push(
        PlayersServices.getPlayer(playerId, ["shortLink"]).then(response => {
          shortLink = response.shortLink?.url || undefined;
        })
      );
    }

    if (settings.includeHashtags) {
      promises.push(
        HashTagsServices.get(playerId).then(response => {
          userHashtags = response.map(hashtag => hashtag.content);
        })
      );
    }

    Promise.all(promises)
      .then(() => {
        let promptHashtags = (prompt?.hashtags || "").split(" ");
        let hashtags = [...promptHashtags, ...userHashtags]
          .filter(Boolean)
          .map(hashtag => (hashtag.startsWith("#") ? hashtag : `#${hashtag}`));
        hashtags = [...new Set(hashtags)];

        var complianceDisclaimer = settings.includeDisclaimer ? userContext.profile?.complianceDisclaimer : undefined;
        resolve({
          shortLink,
          hashtags,
          complianceDisclaimer,
        });
      })
      .catch(err => {
        reject(err);
      });
  });
};

export const buildCaption = (message: string | undefined | null, captionArgs: CaptionArgs): string => {
  var { hashtags, shortLink, complianceDisclaimer } = captionArgs;
  message = message || "";

  let captionBuilder = [message];

  if (shortLink && shortLink.length > 0 && !message.includes(shortLink)) {
    captionBuilder.push(shortLink);
  }

  hashtags = [...new Set(hashtags || [])].filter(hashtag => !message.toLowerCase().includes(hashtag.toLowerCase()));

  if (hashtags.length > 0) {
    captionBuilder.push(hashtags.join(" "));
  }

  if (complianceDisclaimer && complianceDisclaimer.length > 0 && !message.includes(complianceDisclaimer)) {
    captionBuilder.push(complianceDisclaimer);
  }
  return captionBuilder.filter(Boolean).join("\n\n");
};
